import type { ReactNode } from 'react';

import { BpkBox, BpkSpacing } from '@skyscanner-internal/backpack-web/bpk-component-layout';
import BpkText, {
  TEXT_COLORS,
  TEXT_STYLES,
} from '@skyscanner-internal/backpack-web/bpk-component-text';
import { cssModules } from '@skyscanner-internal/backpack-web/bpk-react-utils';
import { useTranslations } from '@web-platform/shared-travel-tools-utils-frontend';

import { HeroIcon } from '../HeroIcon/HeroIcon';
import { TravelBetaLogo } from '../TravelBetaLogo/TravelBetaLogo';

import STYLES from './Hero.module.scss';

const cls = cssModules(STYLES);

type HeroAttributeProps = {
  iconKind: Parameters<typeof HeroIcon>[0]['kind'];
  label: ReactNode;
  description: ReactNode;
};

const HeroAttribute = ({ description, iconKind, label }: HeroAttributeProps) => (
  <li className={cls('heroAttribute')}>
    <HeroIcon kind={iconKind} />
    <div className={cls('heroAttributeContent')}>
      <BpkText tagName="p" textStyle={TEXT_STYLES.heading4} color={TEXT_COLORS.textOnDark}>
        {label}
      </BpkText>
      <BpkText tagName="p" textStyle={TEXT_STYLES.footnote} color={TEXT_COLORS.textOnDark}>
        {description}
      </BpkText>
    </div>
  </li>
);

export const Hero = () => {
  const { translate } = useTranslations();

  return (
    <section className={cls('hero')}>
      <div className={cls('heroInner')}>
        <TravelBetaLogo variant="hero" />
        <BpkBox
          as="h1"
          color={TEXT_COLORS.textOnDark}
          margin={BpkSpacing.None}
          textStyle={{ base: TEXT_STYLES.hero6, 'small-tablet': TEXT_STYLES.hero4 }}
        >
          {translate('travel_tools_hero_title')}
        </BpkBox>
        <ul className={cls('heroAttributes')}>
          <HeroAttribute
            iconKind="journey"
            label={translate('travel_tools_usp_ai_exploration_title')}
            description={translate('travel_tools_usp_ai_exploration_description')}
          />
          <HeroAttribute
            iconKind="experimentation"
            label={translate('travel_tools_usp_early_tools_title')}
            description={translate('travel_tools_usp_early_tools_description')}
          />
          <HeroAttribute
            iconKind="data"
            label={translate('travel_tools_usp_data_insights_title')}
            description={translate('travel_tools_usp_data_insights_description')}
          />
          <HeroAttribute
            iconKind="choices"
            label={translate('travel_tools_usp_curious_travellers_title')}
            description={translate('travel_tools_usp_curious_travellers_description')}
          />
        </ul>
      </div>
    </section>
  );
};
